import DescriptionIcon from '@material-ui/icons/Description';
import GrainIcon from '@material-ui/icons/Grain';
import ReorderIcon from '@material-ui/icons/Reorder';
import SchemeChip from '../core/SchemeChip';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../../application-core/constants';
import Errored from '../../core/Errored';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import Loading from '../../core/Loading';
import Notification from '../../core/Notification';
import OtherProjectLink from '../../core/OtherProjectLink';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, makeCancelable, sortByLangProp } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import ConceptLink from '../core/ConceptLink';
import LabelLink from '../core/LabelLink';
import FloatingLabelRelationshipLink from './FloatingLabelRelationshipsLink';
import FloatingRelationshipLink from './FloatingRelationshipsLink';
import { Typography } from '@material-ui/core';

const loadOptions = {
  "predicates": [
    "http://www.w3.org/2004/02/skos/core#inScheme",
    "http://www.w3.org/2004/02/skos/core#prefLabel",
    "http://www.w3.org/2002/07/owl#deprecated",
    "http://www.w3.org/2008/05/skos-xl#literalForm",
    "http://purl.org/dc/terms/title",
    "http://www.w3.org/2008/05/skos-xl#prefLabel",
    "http://www.w3.org/2004/02/skos/core#narrower",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    "https://grafsync.graphifi.com/schema/gs/status"
  ],
  "tests": [
    {
      "id": "http://www.w3.org/2008/05/skos-xl#prefLabel",
      "join": [{
        "predicate": "http://www.w3.org/2008/05/skos-xl#literalForm"
      }]
    }
  ]
};

class OntologyRelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: true,
      totalRelations: [],
      relatedResources: null,
      unloadedResources: [],
      isErrored: null,
      classList: []
    };
    this.cancellable = null;
    this.cancellable2 = null;
  }

  static equals(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  static getDerivedStateFromProps(props, state) {
    const changed = props.viewLang !== state.sortLang ?
      true
      :
      props.values ?
        !OntologyRelationshipsDisplay.equals(props.values, state.totalRelations)
        :
        false;
    if (!state.isLoading && props.values && changed) {
      return {
        relatedResources: null,
        isLoading: true,
        sortLang: props.viewLang,
        totalRelations: [...props.values],
        unloadedResources: []
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.relatedResources === null && this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    this.collectResourceData();
    if (this.props.searchClasses.length > 0 && this.props.associatedProjectGraph) {
      this.setState({ isSearching: true }, () => {
        const classList = GS.getSubclasses(this.props.searchClasses[0], this.props.associatedProjectGraph);
        this.setState({
          sortLang: this.props.viewLang,
          isSearching: false,
          classList
        });
      });
    }
  }

  loadData(iris, options, graph) {
    if (!graph) graph = this.props.graph;
    this.cancellable = makeCancelable(GS.loadData(iris, options, graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable2) this.cancellable2.cancel();
  }

  collectResourceData() {
    if (this.props.values && this.props.values.length > 0) {
      try {
        // At some point we want to filter the properties loaded for each component
        // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
        GS.handle("value-check");
        if (this.props.values && this.props.values[0] === undefined) throw new Error("There is an issue with the data for this concept.");
        this.loadData(this.props.values, loadOptions)
          .promise
          .then(async () => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const linkedProjects = this.props.associatedProject ? [...this.props.associatedProject].sort() : [];
            let doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
            let allDoesNotExist = [...doesNotExist];
            let lpCounter = 0;
            let graphInfo = {};
            while (doesNotExist.length > 0 && lpCounter < linkedProjects.length) {
              const linkedGraph = getGraphFromProject(linkedProjects[lpCounter]);
              // We also load the linked project too
              await this.loadData([...doesNotExist, linkedProjects[lpCounter]], loadOptions, linkedGraph).promise;
              const doesNowExist = doesNotExist.filter(iri => GS.isLoaded(iri, linkedGraph));
              doesNowExist.forEach(iri => graphInfo[iri] = linkedGraph);
              doesNotExist = doesNotExist.filter(iri => !GS.isLoaded(iri, linkedGraph));
              lpCounter++;
            }
            this.setState({ unloadedResources: allDoesNotExist, graphInfo });
            this.processResources();
            this.setState({ isLoading: false, initialLoad: false });
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      }
      catch (e) {
        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
      }
    }
    else {
      this.setState({ isLoading: false, initialLoad: false });
    }
  }

  processResources() {
    try {
      // Not all resources related to may be loaded (or may not be allowed through lack of permissions)
      const doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
      let resources = this.props.values
        .filter(id => GS.isLoaded(id, this.props.graph))
        .filter(id => {
          const conceptData = GS.data(id, this.props.graph);
          const types = Array.isArray(conceptData.type) ? conceptData.type : [conceptData.type];
          return types.includes("Concept") || types.includes("Label") || types.includes("ConceptScheme") || types.includes("Collection") || types.includes("OrderedCollection");
        })
        .map(id => GS.data(id, this.props.graph));
      const rr = sortByLangProp([...resources], "prefLabel", "literalForm", this.props.viewLang, this.props.defaultLang);
      const resourceList = resources.map(resource => resource.id);
      const nonResources = this.props.values.filter(id => !doesNotExist.includes(id) && !resourceList.includes(id));
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ relatedResources: rr, nonResources });
    }
    catch (e) {
      console.log("Cannot load associated resources");// eslint-disable-line
    }
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "prefLabel" || property === "literalForm") this.processResources();
  };

  render() {

    const { components, isLoading, isReadOnly, defaultLang, associatedProjectGraph, allowEmpty, dialog, searchTypes,
      searchClasses, viewLang, graph, cardinalityAtMax, linkedData, deprecated, exceedsCardinality, info, values, allowIRI, useWorkflow,
      title, projectIRI, matching, includeExistingResource, schemeIRI, resourceIRI, property, canAddValues, associatedProject } = this.props;
    const { relatedResources, unloadedResources, classList, graphInfo, isErrored, initialLoad, nonResources } = this.state;
    const exclusionList = relatedResources ? relatedResources.map(rr => rr.id) : [];

    if (isErrored) return <Errored dataTest="or-error" data={isErrored} />;
    if (isLoading && initialLoad) return <Loading />;

    const FindDialog = dialog;

    return (
      <TitledPanel
        warning={exceedsCardinality ? "Too many relationships. Only " + (info.maxCount || 1) + " expected." : ""}
        dataTest={"property-" + title}
        height="full"
        title={info.name || title}
        actions=
        {(!isReadOnly && !cardinalityAtMax && schemeIRI && canAddValues && !deprecated && !info.inComponentConflict) ?
          <FindDialog
            source={resourceIRI}
            linkedData={info.inComponent ? [] : linkedData}
            useWorkflow={useWorkflow}
            allowEmpty={allowEmpty}
            property={property}
            searchTypes={searchTypes}
            associatedProject={info.inComponent ? [] : associatedProject}
            associatedProjectGraph={associatedProjectGraph}
            searchClasses={[...searchClasses, ...classList]}
            projectIRI={projectIRI}
            componentIRI={schemeIRI}
            graph={graph}
            allowIRI={allowIRI}
            matching={matching}
            viewLang={viewLang}
            defaultLang={defaultLang}
            exclude={[...exclusionList, ...unloadedResources, resourceIRI]}
            onIncludeResource={includeExistingResource} />
          :
          null}>
        {info.description &&
          <div style={{ marginBottom: 12 }}>
            <Typography variant="body2"><DescriptionIcon style={{ transform: 'translateY(4px)' }} fontSize="small" /> <span>{info.description}</span></Typography>
          </div>
        }
        {info.minCount && values.length < info.minCount && <Notification dataTest="warning" error={false} text={"Min. relationships required: " + info.minCount} />}
        {info.inComponentConflict && <Notification dataTest="select-from-conflict" error={false} text={"Select From values from multiple templates are conflicting. Please fix."} />}
        {info.minCountConflict && <Notification dataTest="min-count-conflict" error={false} text={"Min. Count values from multiple templates are conflicting. The largest value is being used."} />}
        {info.maxCountConflict && <Notification dataTest="max-count-conflict" error={false} text={"Max. Count values from multiple templates are conflicting. The smallest value is being used."} />}
        {deprecated && <Notification error={false} text="This property is deprecated" dataTest="deprecated" />}
        {!canAddValues &&
          <Notification error={false} text="This resource no longer has a class valid for this property." dataTest="not-valid" />
        }
        {relatedResources && relatedResources
          .filter(rr => rr.inScheme && rr.inScheme.length > 0)
          .map(rr => {
            // Is link within schemes in project?
            const rrTypes = Array.isArray(rr.type) ? rr.type : [rr.type];
            const isProjectLink = components && rr.inScheme.some(sIRI => components.includes(sIRI));
            if (isProjectLink) {
              if (rrTypes.includes("Label")) {
                const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + projectIRI + "&taxonomy="
                  + (Array.isArray(rr.inScheme) ? rr.inScheme[0] : rr.inScheme)
                  + "&view=label&label=" + rr.id;
                return <LabelLink key={"rel-" + resourceIRI + "-" + rr.id}
                  link={link}
                  id={rr.id}
                  graph={graph}
                  propertyGraph={associatedProjectGraph}
                  isReadOnly={isReadOnly}
                  useWorkflow={useWorkflow}
                  source={resourceIRI}
                  target={rr.id}
                  updateEvent={this.monitorLinkUpdate}
                  property={property}
                  defaultLang={defaultLang} />;
              }
              else {
                const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + projectIRI + "&taxonomy="
                  + (Array.isArray(rr.inScheme) ? rr.inScheme[0] : rr.inScheme)
                  + "&view=concept&concept=" + rr.id;
                return <ConceptLink key={"rel-" + resourceIRI + "-" + rr.id}
                  link={link}
                  id={rr.id}
                  expectedClasses={[...searchClasses, ...classList]}
                  graph={graph}
                  useWorkflow={useWorkflow}
                  propertyGraph={associatedProjectGraph}
                  isReadOnly={isReadOnly}
                  viewLang={viewLang}
                  source={resourceIRI}
                  target={rr.id}
                  updateEvent={this.monitorLinkUpdate}
                  property={property}
                  defaultLang={defaultLang} />;
              }
            }
            else {
              // We shouldn't get to this line because should be an unloaded resource
              return null;
            }
          })}
        {relatedResources && relatedResources
          .filter(rr => !rr.inScheme || rr.inScheme.length === 0)
          .map(rr => {
            const rrTypes = Array.isArray(rr.type) ? rr.type : [rr.type];
            if (rr.type === "ConceptScheme" || rr.type === "Collection" || rr.type === "OrderedCollection") {
              const doesNotHaveExpectedClasses = searchClasses && searchClasses.length > 0 ?
                !searchClasses.some(classIRI => {
                  if (rr.type.includes(classIRI)) return true;
                  const alias = GS.getAlias(classIRI);
                  if (rr.type.includes(alias)) return true;
                  return false;
                })
                :
                false;
              return <SchemeChip key={rr.id}
                id={rr.id}
                viewLang={viewLang}
                defaultLang={defaultLang}
                invalid={doesNotHaveExpectedClasses}
                isReadOnly={isReadOnly}
                propertyGraph={associatedProjectGraph}
                graph={graph}
                avatar={rr.type.includes("OrderedCollection") ?
                  <ReorderIcon data-test="ordered-icon" fontSize="small" />
                  :
                  rr.type.includes("Collection") ?
                    <GrainIcon data-test="unordered-icon" fontSize="small" />
                    :
                    null
                }
                project={projectIRI}
                source={resourceIRI}
                property={property} />;
            }
            else if (rrTypes.includes("Label")) {
              const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + projectIRI + "&view=label&label=" + rr.id;
              // Is a free floating label?
              return <span key={rr.id}>
                <FloatingLabelRelationshipLink
                  id={rr.id}
                  link={link}
                  graph={graph}
                  isReadOnly={isReadOnly}
                  propertyGraph={associatedProjectGraph}
                  source={resourceIRI}
                  target={rr.id}
                  updateEvent={this.monitorLinkUpdate}
                  property={property}
                  viewLang={viewLang}
                  defaultLang={defaultLang} />
              </span>;
            }
            else {
              const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + projectIRI + "&view=concept&concept=" + rr.id;
              // Is concept a free floating concept?
              return <span key={rr.id}>
                <FloatingRelationshipLink
                  id={rr.id}
                  link={link}
                  graph={graph}
                  isReadOnly={isReadOnly}
                  propertyGraph={associatedProjectGraph}
                  source={resourceIRI}
                  target={rr.id}
                  updateEvent={this.monitorLinkUpdate}
                  property={property}
                  viewLang={viewLang}
                  defaultLang={defaultLang} />
              </span>;
            }
          }
          )}
        {nonResources && [...nonResources].sort().map(iri =>
          <ExternalResourceLink
            id={iri}
            key={iri}
            graph={graph}
            propertyGraph={associatedProjectGraph}
            source={resourceIRI}
            isReadOnly={isReadOnly}
            property={property} />
        )}
        {unloadedResources && [...unloadedResources].sort().map((iri) =>
          graphInfo[iri] ?
            <OtherProjectLink
              id={iri}
              key={iri}
              graph={graphInfo[iri]}
              sourceGraph={graph}
              defaultLang={defaultLang}
              viewLang={viewLang}
              propertyGraph={associatedProjectGraph}
              source={resourceIRI}
              isReadOnly={isReadOnly}
              property={property} />
            :
            <ExternalResourceLink
              id={iri}
              key={iri}
              graph={graph}
              propertyGraph={associatedProjectGraph}
              source={resourceIRI}
              isReadOnly={isReadOnly}
              property={property} />
        )}
      </TitledPanel>
    );
  }
}

OntologyRelationshipsDisplay.defaultProps = {
  canAddValues: true,
  cardinalityAtMax: false,
  useWorkflow: false,
  associatedProject: [],
  searchTypes: [],
  values: []
};

OntologyRelationshipsDisplay.propTypes = {
  dialog: PropTypes.object,
  schemeIRI: PropTypes.string,
  canAddValues: PropTypes.bool,
  info: PropTypes.object,
  cardinalityAtMax: PropTypes.bool,
  exceedsCardinality: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  allowIRI: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  deprecated: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  graph: PropTypes.string.isRequired,
  resourceIRI: PropTypes.string.isRequired,
  components: PropTypes.array,
  searchTypes: PropTypes.array,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  linkedData: PropTypes.array,
  associatedProject: PropTypes.array,
  associatedProjectGraph: PropTypes.string.isRequired,
  searchClasses: PropTypes.array,
  property: PropTypes.string,
  title: PropTypes.string,
  matching: PropTypes.bool,
  includeExistingResource: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  projectIRI: PropTypes.string.isRequired
};

export default OntologyRelationshipsDisplay;